import { createContext, useContext, useCallback, useEffect, useState } from 'react'
import { supabase, isConfigured } from '../lib/supabase.js'
import { useAuth } from './AuthContext.jsx'
import { track } from '../lib/analytics.js'

const DataContext = createContext(null)

/* ---------- row <-> app-shape mapping ---------- */
const num = (v) => (v == null || v === '' ? null : Number(v))

const rowToCalc = (r) => ({
  id: r.id,
  createdAt: r.created_at,
  name: r.name,
  vialMg: r.vial_mg,
  waterMl: r.water_ml,
  dose: r.dose,
  doseUnit: r.dose_unit,
  syringe: r.syringe,
  snapshot: r.snapshot,
})

const calcToRow = (c, userId) => ({
  user_id: userId,
  name: c.name,
  vial_mg: num(c.vialMg),
  water_ml: num(c.waterMl),
  dose: num(c.dose),
  dose_unit: c.doseUnit,
  syringe: c.syringe,
  snapshot: c.snapshot ?? null,
})

const rowToEvent = (r) => ({
  id: r.id,
  createdAt: r.created_at,
  calcId: r.calc_id,
  date: r.date,
  time: r.time,
  name: r.name,
  dose: r.dose,
  doseUnit: r.dose_unit,
  units: r.units,
  taken: r.taken,
  takenAt: r.taken_at, // when the dose was checked off (dose-log history)
})

const rowToLog = (r) => ({
  id: r.id, type: r.type, label: r.label, value: Number(r.value), unit: r.unit, date: r.date, createdAt: r.created_at,
})

const rowToDoseLog = (r) => ({
  id: r.id, calcId: r.calc_id, name: r.name, dose: r.dose, doseUnit: r.dose_unit, units: r.units, loggedAt: r.logged_at,
})

const eventToRow = (e, userId) => ({
  user_id: userId,
  calc_id: e.calcId ?? null,
  date: e.date,
  time: e.time ?? null,
  name: e.name,
  dose: num(e.dose),
  dose_unit: e.doseUnit ?? null,
  units: num(e.units),
  taken: e.taken ?? false,
})

export function DataProvider({ children }) {
  const { user } = useAuth()
  const userId = user?.id || null

  const [calcs, setCalcs] = useState([])
  const [events, setEvents] = useState([])
  const [doseLogs, setDoseLogs] = useState([])
  const [wellness, setWellness] = useState([])
  const [notifySettings, setNotifySettings] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Load everything for the current user (and clear on logout).
  useEffect(() => {
    if (!userId || !isConfigured) {
      setCalcs([])
      setEvents([])
      setDoseLogs([])
      setWellness([])
      setNotifySettings(null)
      return
    }
    let active = true
    setLoading(true)
    setError(null)

    Promise.all([
      supabase.from('calculations').select('*').order('created_at', { ascending: false }),
      supabase.from('events').select('*').order('date', { ascending: true }),
      supabase.from('wellness_logs').select('*').order('date', { ascending: true }),
      supabase.from('notification_settings').select('*').maybeSingle(),
      supabase.from('dose_logs').select('*').order('logged_at', { ascending: false }),
    ])
      .then(([calcRes, eventRes, wlRes, nsRes, dlRes]) => {
        if (!active) return
        if (calcRes.error) throw calcRes.error
        if (eventRes.error) throw eventRes.error
        setCalcs(calcRes.data.map(rowToCalc))
        setEvents(eventRes.data.map(rowToEvent))
        // wellness_logs, notification_settings, dose_logs are optional — ignore if not created yet
        if (!wlRes.error && wlRes.data) setWellness(wlRes.data.map(rowToLog))
        if (!nsRes.error) setNotifySettings(nsRes.data || {})
        if (!dlRes.error && dlRes.data) setDoseLogs(dlRes.data.map(rowToDoseLog))
      })
      .catch((e) => active && setError(e.message || 'Failed to load your data.'))
      .finally(() => active && setLoading(false))

    return () => {
      active = false
    }
  }, [userId])

  /* ---------- calculations ---------- */
  const saveCalc = useCallback(
    async (calc) => {
      if (!userId) return { error: 'Not signed in.' }
      const { data, error } = await supabase
        .from('calculations')
        .insert(calcToRow(calc, userId))
        .select()
        .single()
      if (error) return { error: error.message }
      const record = rowToCalc(data)
      setCalcs((list) => [record, ...list])
      track('save_calculation', { peptide: record.name || 'custom' }) // GA4: mark as conversion
      return { record }
    },
    [userId]
  )

  const deleteCalc = useCallback(async (id) => {
    const { error } = await supabase.from('calculations').delete().eq('id', id)
    if (error) return { error: error.message }
    setCalcs((list) => list.filter((c) => c.id !== id))
    return {}
  }, [])

  const renameCalc = useCallback(async (id, name) => {
    const { error } = await supabase.from('calculations').update({ name }).eq('id', id)
    if (error) return { error: error.message }
    setCalcs((list) => list.map((c) => (c.id === id ? { ...c, name } : c)))
    return {}
  }, [])

  // Update any calc fields (edit vial/water/dose, or patch snapshot for tracking).
  const updateCalc = useCallback(async (id, fields) => {
    const row = {}
    if ('name' in fields) row.name = fields.name
    if ('vialMg' in fields) row.vial_mg = num(fields.vialMg)
    if ('waterMl' in fields) row.water_ml = num(fields.waterMl)
    if ('dose' in fields) row.dose = num(fields.dose)
    if ('doseUnit' in fields) row.dose_unit = fields.doseUnit
    if ('syringe' in fields) row.syringe = fields.syringe
    if ('snapshot' in fields) row.snapshot = fields.snapshot
    const { data, error } = await supabase.from('calculations').update(row).eq('id', id).select().single()
    if (error) return { error: error.message }
    const rec = rowToCalc(data)
    setCalcs((list) => list.map((c) => (c.id === id ? rec : c)))
    return { record: rec }
  }, [])

  /* ---------- events ---------- */
  const addEvent = useCallback(
    async (event) => {
      if (!userId) return { error: 'Not signed in.' }
      const { data, error } = await supabase
        .from('events')
        .insert(eventToRow(event, userId))
        .select()
        .single()
      if (error) return { error: error.message }
      const record = rowToEvent(data)
      setEvents((list) => [...list, record])
      return { record }
    },
    [userId]
  )

  const addEvents = useCallback(
    async (list) => {
      if (!userId) return { error: 'Not signed in.' }
      const { data, error } = await supabase
        .from('events')
        .insert(list.map((e) => eventToRow(e, userId)))
        .select()
      if (error) return { error: error.message }
      const records = data.map(rowToEvent)
      setEvents((cur) => [...cur, ...records])
      return { records }
    },
    [userId]
  )

  const toggleTaken = useCallback(
    async (id) => {
      const current = events.find((e) => e.id === id)
      if (!current) return
      const next = !current.taken
      // Stamp when it was logged so the dose-log history can show a real time.
      const takenAt = next ? new Date().toISOString() : null
      setEvents((list) => list.map((e) => (e.id === id ? { ...e, taken: next, takenAt } : e))) // optimistic
      const { error } = await supabase.from('events').update({ taken: next, taken_at: takenAt }).eq('id', id)
      if (error) {
        setEvents((list) => list.map((e) => (e.id === id ? { ...e, taken: current.taken, takenAt: current.takenAt } : e))) // revert
      }
    },
    [events]
  )

  const deleteEvent = useCallback(async (id) => {
    const prev = events
    setEvents((list) => list.filter((e) => e.id !== id)) // optimistic
    const { error } = await supabase.from('events').delete().eq('id', id)
    if (error) setEvents(prev) // revert
  }, [events])

  // Remove the scheduled doses for a saved calculation. By default keeps any
  // already marked "taken" so re-scheduling doesn't wipe logged history.
  const deleteEventsByCalc = useCallback(
    async (calcId, { keepTaken = true } = {}) => {
      if (!calcId) return {}
      const prev = events
      setEvents((list) => list.filter((e) => !(e.calcId === calcId && (!keepTaken || !e.taken))))
      let q = supabase.from('events').delete().eq('calc_id', calcId)
      if (keepTaken) q = q.eq('taken', false)
      const { error } = await q
      if (error) {
        setEvents(prev)
        return { error: error.message }
      }
      return {}
    },
    [events]
  )

  /* ---------- wellness progress logs ---------- */
  const addWellnessLog = useCallback(async (log) => {
    if (!userId) return { error: 'Not signed in.' }
    const { data, error } = await supabase
      .from('wellness_logs')
      .insert({ user_id: userId, type: log.type, label: log.label ?? null, value: num(log.value), unit: log.unit ?? null, date: log.date })
      .select().single()
    if (error) return { error: error.message }
    setWellness((list) => [...list, rowToLog(data)].sort((a, b) => a.date.localeCompare(b.date)))
    return { record: rowToLog(data) }
  }, [userId])

  const deleteWellnessLog = useCallback(async (id) => {
    const prev = wellness
    setWellness((list) => list.filter((l) => l.id !== id))
    const { error } = await supabase.from('wellness_logs').delete().eq('id', id)
    if (error) setWellness(prev)
  }, [wellness])

  /* ---------- notification settings ---------- */
  const updateNotifySettings = useCallback(async (patch) => {
    if (!userId) return { error: 'Not signed in.' }
    const { data, error } = await supabase
      .from('notification_settings')
      .upsert({ user_id: userId, ...patch, updated_at: new Date().toISOString() })
      .select().single()
    if (error) return { error: error.message }
    setNotifySettings(data)
    return { record: data }
  }, [userId])

  /* ---------- dose log (timestamped "I took a dose" history) ---------- */
  const logDose = useCallback(
    async ({ calcId = null, name, dose, doseUnit, units }) => {
      if (!userId) return { error: 'Not signed in.' }
      const { data, error } = await supabase
        .from('dose_logs')
        .insert({ user_id: userId, calc_id: calcId, name: name ?? null, dose: num(dose), dose_unit: doseUnit ?? null, units: num(units) })
        .select()
        .single()
      if (error) return { error: error.message }
      const record = rowToDoseLog(data)
      setDoseLogs((list) => [record, ...list])
      track('log_dose', { peptide: record.name || 'dose' })
      return { record }
    },
    [userId]
  )

  const deleteDoseLog = useCallback(async (id) => {
    const { error } = await supabase.from('dose_logs').delete().eq('id', id)
    if (error) return { error: error.message }
    setDoseLogs((list) => list.filter((d) => d.id !== id))
    return {}
  }, [])

  const value = {
    calcs,
    events,
    doseLogs,
    logDose,
    deleteDoseLog,
    wellness,
    loading,
    error,
    addWellnessLog,
    deleteWellnessLog,
    notifySettings,
    updateNotifySettings,
    saveCalc,
    deleteCalc,
    renameCalc,
    updateCalc,
    addEvent,
    addEvents,
    toggleTaken,
    deleteEvent,
    deleteEventsByCalc,
  }
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export const useUserData = () => useContext(DataContext)
